import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { dashboardWidgetService } from "../services/dashboardWidgetService";
import { dashboardWidgetDisplayId } from "./DashboardWidgetCommonStore";
import { DashboardWidget } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class DashboardWidgetViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = dashboardWidgetService;
  dashboardWidgetInstance: DashboardWidget | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  widgetKeyValue: string = "";
  titleValue: string = "";
  descriptionValue: string = "";
  categoryValue: string = "";
  componentValue: string = "";
  dataFetcherKeyValue: string = "";
  defaultConfigValue: string = "{}";
  iconValue: string = "";
  minWidthValue: number = 0;
  minHeightValue: number = 0;
  refreshIntervalValue: number | undefined = undefined;
  requiredPermissionsValue: string = "";
  enabledValue: boolean = false;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadDashboardWidgetById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.dashboardWidgetOne({ id });
    this.dashboardWidgetInstance = result.dashboardWidgetOne;
    if (this.dashboardWidgetInstance) {
      this.loadValues(this.dashboardWidgetInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.dashboardWidgetInstance) {
      return dashboardWidgetDisplayId(
        this.dashboardWidgetInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.DashboardWidgetSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.dashboardWidgetInstance = null;
    this.widgetKeyValue = "";
    this.titleValue = "";
    this.descriptionValue = "";
    this.categoryValue = "";
    this.componentValue = "";
    this.dataFetcherKeyValue = "";
    this.defaultConfigValue = "{}";
    this.iconValue = "";
    this.minWidthValue = 0;
    this.minHeightValue = 0;
    this.refreshIntervalValue = undefined;
    this.requiredPermissionsValue = "";
    this.enabledValue = false;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelDashboardWidget() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editDashboardWidget() {
    const id = this.dashboardWidgetInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/dashboardWidget/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printDashboardWidget(handlePrint: () => void): void {
    handlePrint();
  }

  *closeDashboardWidget(): any {
    yield this.close();
  }

  loadValues(instance: DashboardWidget) {
    this.idValue = instance.id;
    this.widgetKeyValue = instance.widgetKey;
    this.titleValue = instance.title;
    this.descriptionValue = instance.description || "";
    this.categoryValue = instance.category;
    this.componentValue = instance.component;
    this.dataFetcherKeyValue = instance.dataFetcherKey;
    this.defaultConfigValue = instance.defaultConfig;
    this.iconValue = instance.icon || "";
    this.minWidthValue = instance.minWidth;
    this.minHeightValue = instance.minHeight;
    this.refreshIntervalValue = instance.refreshInterval || undefined;
    this.requiredPermissionsValue = instance.requiredPermissions;
    this.enabledValue = instance.enabled;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadDashboardWidgetById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
